import { useParams } from "react-router-dom";
import { Property_List, Property_Status_Enum, useDeleteQueriesPropertyResultMutation, useGetPropertyBySlugLazyQuery, useGetPropertyResultAggregateForNlqLazyQuery, useGetSearchSlugByIdLazyQuery, useInsertQueriesPropertyResultMutation } from "../../api/generated/graphql";
import { IconButton, Link, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useState, useEffect } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

function PropertyGroup() {
  const params = useParams();
  const [searchSlug, setSearchSlug] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [nlqId, setNlqId] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [propertyResults, setPropertyResults] = useState<Record<string, number>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [missingQuery, setMissingQuery] = useState(false);

  // Pagination state with URL parameters
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;


  const [getProperties] = useGetSearchSlugByIdLazyQuery();
  const [getPropertiesBySlug] = useGetPropertyBySlugLazyQuery();
  const [getPropertyResultAggregateForNlq] = useGetPropertyResultAggregateForNlqLazyQuery();
  const [insertQueriesPropertyResult] = useInsertQueriesPropertyResultMutation();
  const [deleteQueriesPropertyResult] = useDeleteQueriesPropertyResultMutation();

  // Initialize from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");

    if (pageParam && !isNaN(parseInt(pageParam)) && parseInt(pageParam) > 0) {
      setCurrentPage(parseInt(pageParam));
    }
  }, []);

  // Update URL when page changes
  const updateURL = (page: number) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());

    const newUrl =
      window.location.pathname +
      (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState({}, "", newUrl);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURL(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Format price number for better readability
  const formatPrice = (price: number | string | null | undefined): string => {
    if (price === null || price === undefined) return '0';

    const numPrice = typeof price === 'string' ? parseFloat(price) : price;

    if (isNaN(numPrice)) return '0';

    // Format with commas for thousands and handle decimals
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numPrice);
  };

  const addPropertyToCollection = (propertyList: Property_List) => {
    try {
      insertQueriesPropertyResult({
        variables: {
          object: {
            property_id: propertyList.property?.id as string,
            natural_language_query_id: nlqId,
            price: propertyList.price,
            property_last_seen: propertyList.property?.last_seen,
          },
        },
      }).then(() => {
        handleClearSearch();
        refetch();
      });
    } catch (error) {
      console.error('Error inserting property result', error);
    }
  };

  const removePropertyFromCollection = async (propertyId: string) => {
    try {
      await deleteQueriesPropertyResult({
        variables: {
          where: {
            property_id: {_eq: propertyId},
            natural_language_query_id: {_eq: nlqId},
          },
        },
      }).then(() => {
        // Just refetch to update the list, don't clear search or reset page
        refetch();
      });
    } catch (error) {
      console.error('Error deleting property result', error);
    }
  };

  // Function to extract slug from URL
  const extractSlugFromUrl = (url: string): string => {
    try {
      // Remove any leading/trailing whitespace
      const trimmedUrl = url.trim();
      
      // If it's already a slug (no protocol), return as is
      if (!trimmedUrl.includes('://') && !trimmedUrl.startsWith('http')) {
        return trimmedUrl;
      }
      
      // Parse the URL
      const urlObj = new URL(trimmedUrl);
      const pathname = urlObj.pathname;
      
      // Extract slug from path like /al/property/slug-name
      const pathParts = pathname.split('/').filter(part => part.length > 0);
      
      // Find the 'property' part and get the next part as slug
      const propertyIndex = pathParts.findIndex(part => part === 'property');
      if (propertyIndex !== -1 && propertyIndex + 1 < pathParts.length) {
        return pathParts[propertyIndex + 1];
      }
      
      // If no 'property' found, return the last part of the path
      if (pathParts.length > 0) {
        return pathParts[pathParts.length - 1];
      }
      
      return trimmedUrl;
    } catch (error) {
      // If URL parsing fails, return the original string
      return url.trim();
    }
  };

  const handleSearch = () => {
    if (searchSlug.trim()) {
      const extractedSlug = extractSlugFromUrl(searchSlug);
      setSearchSlug(extractedSlug);
      setIsSearchMode(true);
      // Reset to page 1 when searching
      setCurrentPage(1);
      updateURL(1);
      refetch();
    }
  };

  const handleClearSearch = () => {
    setSearchSlug("");
    setIsSearchMode(false);
    // Reset to page 1 when clearing search
    setCurrentPage(1);
    updateURL(1);
    refetch();
  };
   
    const {
      data,
      isError,
      isLoading,
      refetch,
    } = useQuery({
      queryKey: [
        "properties",
        {
          currentPage, //refetch when page changes
          isSearchMode, //refetch when search mode changes
          searchSlug: isSearchMode ? searchSlug : null, // Only include searchSlug when in search mode
        },
      ],
      queryFn: async () => {
        
        if (isSearchMode && searchSlug.trim()) {
          // Search mode: get properties by slug with server-side pagination
          const { data: searchData, error } = await getPropertiesBySlug({
            variables: {
              slug: searchSlug.trim(),
              limit: pageSize,
              offset: (currentPage - 1) * pageSize,
            },
            fetchPolicy: "network-only",
          });

          if (error || !searchData || !searchData.url_slug || searchData.url_slug.length === 0) {
            return {
              property_list: [],
              property_list_aggregate: {
                aggregate: {
                  count: 0,
                },
              },
            };
          }
          
          const propertyLists = searchData.url_slug[0].property.property_lists || [];
          const totalCount = searchData.url_slug[0].property.property_lists_aggregate?.aggregate?.count || 0;


          return {
            property_list: propertyLists,
            property_list_aggregate: {
              aggregate: {
                count: totalCount,
              },
            },
          };
        } else {
        const { data: propData, error } = await getProperties({
          variables: {
            id: params.id as string,
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
          },
          fetchPolicy: "network-only",
        });
  
        if (error || !propData) {
          return {
            property_list: [],
            property_list_aggregate: {
              aggregate: {
                count: 0,
              },
            },
          };
        }

        const latestQuery = propData.search_slugs_by_pk?.search_session?.natural_language_queries?.[0];
        if (!latestQuery) {
          setNlqId(null);
          setTitle(propData.search_slugs_by_pk?.search_session?.title || null);
          setMissingQuery(true);
          return {
            property_list: [],
            property_list_aggregate: {
              aggregate: {
                count: 0,
              },
            },
          };
        }

        setMissingQuery(false);
        const propertyLists = latestQuery.queries_property_results.map((result) => result.property?.main_property);
        setNlqId(latestQuery.id);
        setTitle(propData.search_slugs_by_pk?.search_session?.title || null);
        const propertyListAggregate = latestQuery.queries_property_results_aggregate?.aggregate?.count || 0;


        return {
          property_list: propertyLists,
          property_list_aggregate: {
            aggregate: {
              count: propertyListAggregate,
            },
          },
        };
        }
      },
      placeholderData: keepPreviousData, //don't go to 0 rows when refetching or paginating to next page
    });

  // Fetch property results for all properties when data changes
  useEffect(() => {
    const fetchPropertyResults = async () => {
      if (!nlqId || !data?.property_list || data.property_list.length === 0) {
        return;
      }

      const results: Record<string, number> = {};
      
      for (const property of data.property_list) {
        if (property?.property?.id && isSearchMode) {
          try {
            const res = await getPropertyResultAggregateForNlq({
              variables: {
                nlqId: nlqId,
                propertyId: property.property.id,
              },
              fetchPolicy: "no-cache",
            });
            results[property.property.id] = res.data?.queries_property_result_aggregate?.aggregate?.count || 0;
          } catch (err) {
            console.error('Error getting property result aggregate for nlq', err);
            results[property.property.id] = 0;
          }
        }
      }
      
      setPropertyResults(results);
    };

    fetchPropertyResults();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nlqId, data?.property_list]);

  // Calculate pagination info
  const totalCount = data?.property_list_aggregate?.aggregate?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
            } else {
      // Show first page, last page, and pages around current page
      if (currentPage <= 4) {
        // Near the beginning
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };


  return (
    <div className="flex flex-col w-full h-screen p-2">
      <div className="w-full px-10 pb-5 mx-auto">
        {/* Header */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {title}
            </h1>
        </div>
        {/* Search Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1 max-w-md">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by property slug or paste full URL..."
                  value={searchSlug}
                  onChange={(e) => setSearchSlug(e.target.value)}
                  className="flex-1 px-4 py-2 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 h-11 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 font-medium">
                  Search
                </button>
                {isSearchMode && (
                  <button
                    onClick={handleClearSearch}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium">
                    Clear
                  </button>
                )}
              </div>
              {isSearchMode && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing properties for slug: <span className="font-semibold">{searchSlug}</span>
                  {searchSlug.includes('://') && (
                    <span className="text-blue-600 ml-2">(URL parsed automatically)</span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
        {/* Custom Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    URL Slug
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Main
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Property Source
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  // Loading skeleton rows
                  Array.from({ length: pageSize }, (_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-28"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded w-12"></div>
                      </td>
                    </tr>
                  ))
                ) : (data?.property_list as Property_List[])?.length > 0 ? (
                  // Actual data rows
                  (data?.property_list as Property_List[]).map((property) => {
                    // Get the result count from state instead of calling API
                    const propertyId = property.property?.id;
                    const resultCount = propertyId ? propertyResults[propertyId] || 0 : 0;

                    const handleToggleFeatured = () => {
                      if (!resultCount && isSearchMode) {
                        addPropertyToCollection(property);
                      } else {
                        setSelectedPropertyId(property.property?.id);
                        setShowConfirmDialog(true);
                      }
                    };

                    return (
                      <tr
                        key={property.id}
                        className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="text-sm text-blue-600 hover:text-blue-800 max-w-xs truncate">
                            <Link href={`${process.env.REACT_APP_BASE_URL}/al/property/${property.property?.url_slugs[0]?.slug}`} target="_blank">
                              {property.property?.url_slugs[0]?.slug}
                            </Link>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {property.property?.main_listing_id === property.id ? 'Yes' : 'No'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {property.property_type}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {property.property_status === Property_Status_Enum.ForRent ? 'For rent' : 'For sale'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 text-nowrap">
                            {property.price_currency} {formatPrice(property.price)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 w-32 truncate">
                            {property.property_source?.agency?.name || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Tooltip title={!resultCount && isSearchMode ? "Add to collection" : "Remove from collection"}>
                            <IconButton
                              onClick={handleToggleFeatured}
                              color={!resultCount && isSearchMode ? "primary" : "error"}
                              size="small"
                            >
                              {!resultCount && isSearchMode ? <AddCircleIcon /> : <DeleteIcon />}
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  // Empty state row
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {isError
                          ? "Error loading data"
                          : missingQuery
                            ? "Missing query, cannot regenerate properties"
                          : isSearchMode
                            ? `No properties found for slug "${searchSlug}"`
                            : "No properties found"}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              {/* Mobile pagination */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasPrevPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasNextPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination">
                  {/* Previous button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                      hasPrevPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Previous</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }

                    const isCurrentPage = pageNum === currentPage;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum as number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}>
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Next button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                      hasNextPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Next</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Discard Confirmation Modal */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove from Featured?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this property from featured properties section?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedPropertyId) {
                    await removePropertyFromCollection(selectedPropertyId);
                    setShowConfirmDialog(false);
                    setSelectedPropertyId(null);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyGroup;
